import { ReactElement, useCallback, useState } from 'react';
import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import {
  Contract,
  ContractItem,
  ContractItemPriceRaiseStatus,
  getContractItemPriceRaiseDto,
  useAcceptContractItemPriceRaiseMutation,
  useUpdateContractItemPriceRaiseMutation,
} from 'entities/Contract';
import { AcceptPriceRaiseModal } from '../../ui/AcceptPriceRaiseModal';

export interface OpenAcceptPriceRaiseParams {
  contract: Contract;
  newPrice: number;
  currentPrice?: number;
  contractItem?: Nullable<ContractItem>;
  priceRaiseDate?: string;
  contractItemPriceRaiseId?: string;
}

interface HookApi {
  openAcceptPriceRaise: (params: OpenAcceptPriceRaiseParams) => void;
  acceptPriceRaiseModal: ReactElement;
}

export const useAcceptPriceRaise = (): HookApi => {
  const { t } = useAppTranslation(['contracts', 'common']);
  const currencySymbol = useGetCurrencySymbol();

  const [params, setParams] = useState<Nullable<OpenAcceptPriceRaiseParams>>(null);
  const [acceptPriceRaise, { isLoading: isCreating }] = useAcceptContractItemPriceRaiseMutation();
  const [updatePriceRaise, { isLoading: isUpdating }] = useUpdateContractItemPriceRaiseMutation();

  const openAcceptPriceRaise = useCallback((nextParams: OpenAcceptPriceRaiseParams): void => {
    setParams(nextParams);
  }, []);

  const closeAcceptPriceRaise = useCallback((): void => {
    setParams(null);
  }, []);

  const confirmAcceptPriceRaise = useCallback(async (): Promise<void> => {
    if (!params) {
      return;
    }

    const dto = getContractItemPriceRaiseDto(params.contract, params.newPrice, params.currentPrice, params.contractItem);

    try {
      if (params.contractItemPriceRaiseId) {
        await updatePriceRaise({
          contractItemPriceRaiseId: params.contractItemPriceRaiseId,
          ...dto,
          status: ContractItemPriceRaiseStatus.ACCEPTED,
        }).unwrap();
      } else {
        await acceptPriceRaise({
          contractId: params.contract.contractId,
          ...dto,
        }).unwrap();
      }

      showNotification('info', t('Success', { ns: 'common' }), t('Price raise has been accepted'));
      closeAcceptPriceRaise();
    } catch {
      showNotification('error', t('Error', { ns: 'common' }), t('Error when accepting price raise'));
    }
  }, [acceptPriceRaise, closeAcceptPriceRaise, params, t, updatePriceRaise]);

  return {
    openAcceptPriceRaise,
    acceptPriceRaiseModal: (
      <AcceptPriceRaiseModal
        isOpened={Boolean(params)}
        contract={params?.contract || null}
        proposedPrice={params?.newPrice ?? null}
        currencySymbol={currencySymbol}
        extras={params || undefined}
        isLoading={isCreating || isUpdating}
        onCancel={closeAcceptPriceRaise}
        onConfirm={confirmAcceptPriceRaise}
      />
    ),
  };
};
